import * as React from 'react';
import { translate } from 'react-i18next';
const { connect } = require('react-redux');
const { asyncConnect } = require('redux-connect');
import { browserHistory } from 'react-router';
import { Field, reduxForm, InjectedFormProps, getFormSyncErrors } from 'redux-form';
import { IStore } from 'redux/IStore';
import { getUserPreferences, putUserPreferences } from 'modules/user/userpreferences';
import { changeLanguage } from 'redux/modules/user/userlanguage';
import { IUserPreferences, IUserPreferencesAction } from 'models/user/userpreferences';
import * as c from 'models/constants';
import { IUserLanguage } from 'models/user/userlanguage';
import { ILanguage, IDomainValue } from 'models/common';
import { ErrorSummary, Button, LoadingOverlayWrapper } from 'components';
import { PortalSelectMenu } from 'components/FormControls';
import { IDropdownData } from 'models/common';
import { RightColumnPortalPage, PortalPageSection, ButtonsSection, PageTitle } from 'components/PortalStructure';
import { preventRouterLeave } from 'helpers/hooks';
import { InjectedRouter } from 'react-router';
import { ManageUserWrapper } from 'containers';

interface IProps extends InjectedFormProps {
	userPreferences: IUserPreferences;
	initialValues: IUserPreferences;
	languageDomainValues: ILanguage[];
	location: any;
	router: InjectedRouter;
	route: any;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	getUserPreferences: () => IUserPreferencesAction;
	putUserPreferences: (userPreferences: IUserPreferences) => IUserPreferencesAction;
	changeLanguage: (language: number, updateUserPreferences: boolean) => IUserLanguage;
	t?: (key: string) => string;
}

@asyncConnect([
	{
		promise: ({ store: { dispatch } }) => {
			return dispatch(getUserPreferences());
		},
	},
])
@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class UserPreferencesForm extends React.Component<IProps, {}> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.cancelClick = this.cancelClick.bind(this);
	}

	private onSubmit(form: IUserPreferences) {
		if (form.languageKey) {
			this.props.changeLanguage(parseInt(form.languageKey.toString(), 10), false);
		}
		this.props.putUserPreferences(form);
	}

	private cancelClick() {
		browserHistory.push('/');
	}

	private mapErrorKeyToMessage(key: string) {
		switch (key) {
			default:
				return key;
		}
	}

	/** --------------RENDER ----------------------- */
	public render() {
		const { pristine, submitting, handleSubmit, t, languageDomainValues, formErrors, submitFailed, location } =
			this.props;
		const { timeZones } = this.props.initialValues;
		const { error, message } = this.props.userPreferences;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error,
			message,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const timezones: IDropdownData[] = timeZones.map((item: any) => {
			return { key: item.key, value: item.value };
		});

		const languages = [];
		let i: number;
		if (languageDomainValues && languageDomainValues.length > 0) {
			for (i = 0; i < 4; i++) {
				//remove when CH and RU languages are supported
				if (languageDomainValues[i] && languageDomainValues[i].key && languageDomainValues[i].culture) {
					const valueKey = `DV_Languages_${languageDomainValues[i].key}`;
					let value = t(valueKey);
					if (value === valueKey) {
						if (languageDomainValues[i].value) {
							value = languageDomainValues[i].value;
						} else if (languageDomainValues[i].culture) {
							value = languageDomainValues[i].culture;
						}
					}
					languages.push({
						key: languageDomainValues[i].key,
						value,
					});
				}
			}

			languages.sort(function (a: IDomainValue, b: IDomainValue) {
				if (a.value < b.value) {
					return -1;
				}
				if (a.value > b.value) {
					return 1;
				}
				return 0;
			});
		}

		return (
			<ManageUserWrapper>
				<RightColumnPortalPage>
					<PageTitle title={t('User_UserPreferences_Title')} />
					<div className="userPreferencesForm">
						<LoadingOverlayWrapper
							active={this.props.userPreferences.isFetching}
							spinner={true}
							text={t('BSPortal_Common_Loading')}
							background="rgba(0, 0, 0, 0.1)"
						>
							<form onSubmit={handleSubmit(this.onSubmit)}>
								<div>
									<ErrorSummary {...errorSummaryProps} />
								</div>
								<Field name="id" component="input" type="hidden" />
								<PortalPageSection title={t('User_UserPreferences_Section_Localization_Title')}>
									<div className="row">
										<div className="col-md-6">
											<Field
												name="languageKey"
												component={PortalSelectMenu}
												parse={(value) => (value ? Number(value) : null)}
												items={languages}
												type="number"
												isRequired={true}
												label={t('User_UserPreferences_Label_Language')}
												searchable={false}
											/>
										</div>
										<div className="col-md-6">
											<Field
												name="zoneInfo"
												component={PortalSelectMenu}
												items={timezones}
												label={t('User_UserPreferences_Label_TimeZone')}
											/>
										</div>
									</div>
								</PortalPageSection>
								<ButtonsSection>
									<div className="row">
										<div className="col-sm-12">
											<div className="floatRight">
												<Button type="submit" disabled={pristine || submitting}>
													{t('BSPortal_Common_Save')}
												</Button>
												<div className="clear" />
											</div>
											<div className="clear" />
										</div>
									</div>
								</ButtonsSection>
							</form>
						</LoadingOverlayWrapper>
					</div>
				</RightColumnPortalPage>
			</ManageUserWrapper>
		);
	}
}

let UserPreferences = reduxForm({
	form: c.FORM_NAME_USERPREFERENCES,
	enableReinitialize: true,
})(UserPreferencesForm);

UserPreferences = connect(
	(state: IStore) => ({
		initialValues: state.userPreferences,
		userPreferences: state.userPreferences,
		languageDomainValues: state.domainValues.languages,
		formErrors: getFormSyncErrors(c.FORM_NAME_USERPREFERENCES)(state),
	}),
	(dispatch) => ({
		putUserPreferences: (form: IUserPreferences) => dispatch(putUserPreferences(form)),
		changeLanguage: (language: number, updateUserPreferences: boolean) =>
			dispatch(changeLanguage(language, updateUserPreferences)),
	}),
)(UserPreferences);

export { UserPreferences };
